<template>
  <div class="multiselect-bootstrap-helper"
       :class="validationClass">
    <VueMultiselect ref="multiselect"
                    :options="options"
                    :selected-label="selectedLabel"
                    :disabled="disabled || readOnly"
                    :multiple="multiple"
                    :searchable="searchableValue"
                    :allow-empty="allowEmpty"
                    :placeholder="placeholder"
                    :clear-on-select="clearOnSelect"
                    :close-on-select="closeOnSelect"
                    :group-select="groupSelect"
                    :group-values="groupValues"
                    :group-label="groupLabel"
                    :preserve-search="preserveSearch"
                    :hide-selected="hideSelected"
                    :track-by="trackBy"
                    :label="label"
                    :class="multiselectClass"
                    :loading="loading"
                    :model-value="modelValue"
                    :custom-label="customLabel"
                    :open-direction="openDirection"
                    :show-labels="showLabels"
                    :max-height="maxHeight"
                    :reset-after="resetAfter"
                    :limit="groupLimit"
                    :limit-text="(count) => `${count + ' ' + tr('systemFilter.MORE')}`"
                    :internal-search="internalSearch"
                    :options-limit="optionsLimit"
                    deselect-label=""
                    select-label=""
                    @open="onOpen"
                    @close="onClose"
                    @select="onSelect"
                    @remove="onRemove"
                    @update:model-value="onInput"
                    @search-change="onSearchChange">
      <template #option="{ option }">
        <span v-if="multiple && showCheckboxes && !option.$isLabel">
          <Icon :name="getCheckboxIconName(option)"
                :clickable="true"
                :title="tr(getOptionLabel(option))"
                class="mr-2">
          </Icon>
          <slot name="option"
                :option="option">
            <span class="text-truncate w-90 d-inline-block pt-1"
                  :title="tr(getOptionLabel(option))">
              {{ tr(getOptionLabel(option)) }}
            </span>
          </slot>
        </span>
        <span v-else
              class="text-truncate"
              :title="tr(getOptionLabel(option))">
          <slot name="option"
                :option="option">
            {{ tr(getOptionLabel(option)) }}
          </slot>
        </span>
      </template>
      <template #singleLabel="{ option }">
        <slot name="singleLabel"
              :option="option">
        </slot>
      </template>
      <template #noResult="{ noResult }">
        <slot name="noResult"
              :no-result="noResult">
          <span class="text-truncate"
                :title="tr('common.SEARCH_QUERY_NO_RESULT_FOUND')">
            {{ tr('common.SEARCH_QUERY_NO_RESULT_FOUND') }}
          </span>
        </slot>
      </template>
      <template #noOptions="{ noOptions }">
        <slot name="noOptions"
              :no-options="noOptions">
          <span class="text-truncate"
                :title="tr('common.SEARCH_QUERY_NO_OPTIONS_FOUND')">
            {{ tr('common.SEARCH_QUERY_NO_OPTIONS_FOUND') }}
          </span>
        </slot>
      </template>
      <template #tag="{ tag }">
        <slot name="tag"
              :tag="tag">
        </slot>
      </template>
      <template #selection="{ values, search, isOpen }">
        <slot name="selection"
              :selection="{ values, search, isOpen, onInput, options, readOnly }">
        </slot>
      </template>
      <template #afterList="{ afterList }">
        <slot name="afterList"
              :after-list="{ afterList }">
        </slot>
      </template>
    </VueMultiselect>
    <div v-if="showAction">
      <a ref="action"
         class="action"
         @click="onActionClicked">
        <span class="multiselect__option">
          <span>{{ action }}</span>
        </span>
      </a>
    </div>
  </div>
</template>

<script>
  import VueMultiselect from 'vue-multiselect';
  import Icon from '../layout/Icon.vue';
  import translator from '~/zf/frontend/common/mixins/translator';
  import BootstrapVariant from '~/zf/frontend/vendor/bootstrap/BootstrapVariant';

  export default {
    name: 'Selection',
    compatConfig: {
      MODE: 3,
    },
    components: {
      VueMultiselect,
      Icon,
    },
    mixins: [translator],
    props: {
      action: {
        type: String,
        default: '',
      },
      allowEmpty: {
        type: Boolean,
        default: false,
      },
      multiple: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      clearOnSelect: {
        type: Boolean,
        default: true,
      },
      closeOnSelect: {
        type: Boolean,
        default: true,
      },
      preserveSearch: {
        type: Boolean,
        default: false,
      },
      groupValues: {
        type: String,
      },
      groupLabel: {
        type: String,
      },
      groupSelect: {
        type: Boolean,
        default: false,
      },
      hideSelected: {
        type: Boolean,
        default: false,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      optionsLimit: {
        type: Number,
        default: 1000,
      },
      placeholder: {
        type: String,
        default: '',
      },
      selectedLabel: {
        type: String,
        default: '',
      },
      label: {
        type: String,
        default: '',
      },
      trackBy: {
        type: String,
        default: '',
      },
      customLabel: {
        type: Function,
      },
      classProp: {
        type: Object,
      },
      maxHeight: {
        type: Number,
      },
      openDirection: {
        type: String,
      },
      showCheckboxes: {
        type: Boolean,
        default: false,
      },
      showLabels: {
        type: Boolean,
      },
      modelValue: [Object, Array, String, Number, null],
      options: {
        type: Array,
        required: true,
      },
      searchable: {
        type: Boolean,
        default: undefined,
      },
      state: {
        type: Boolean,
        default: null,
      },
      resetAfter: {
        type: Boolean,
        default: false,
      },
      size: {
        type: String,
        default: '',
      },
      internalSearch: {
        type: Boolean,
        default: true,
      },
      readOnly: {
        type: Boolean,
        required: false,
      },
      groupLimit: {
        type: Number,
        required: false,
      },
    },
    emits: ['open', 'action-clicked', 'close', 'select', 'remove', 'update:model-value', 'search-change', 'input'],
    data() {
      return {
        searchableLength: 8,
        showAction: false,
      };
    },
    computed: {
      multiselectClass() {
        return [
          this.classProp,
          {
            'selection-success': this.state === true,
            'selection-error': this.state === false,
          },
          {
            'multiselect-sm': this.size === BootstrapVariant.SMALL,
          },
          {
            'multiselect--readonly': this.readOnly,
          },
        ];
      },
      validationClass() {
        return [
          {
            'is-valid': this.state === true,
            'is-invalid': this.state === false,
          },
        ];
      },
      searchableValue() {
        if (this.searchable !== undefined) {
          return this.searchable;
        }
        return this.options.length > this.searchableLength;
      },
    },
    watch: {
      options() {
        this.updateActionOffset();
      },
      readOnly: {
        async handler(isReadOnly) {
          await this.$nextTick();
          if (isReadOnly) {
            this.$refs.multiselect.$el.classList.remove('multiselect--disabled');
          }
        },
        immediate: true,
      },
    },
    methods: {
      open() {
        this.$refs.multiselect.$el.focus();
      },
      activate() {
        this.$refs.multiselect.activate();
      },
      getCheckboxIconName(option) {
        const optionIsSelected = this.modelValue.includes(option);
        return optionIsSelected ? 'checkbox-checked' : 'checkbox-unchecked';
      },
      getOptionLabel(option) {
        if (this.customLabel) {
          return this.customLabel(option);
        }
        if (option.isTag) {
          return option.label;
        }
        if (option.$isLabel) {
          return option.$groupLabel;
        }
        return option[this.label] ?? option;
      },
      updateActionOffset() {
        this.$nextTick(() => {
          const actionSelector = this.$refs.action;

          if (actionSelector === undefined || actionSelector === null) {
            return;
          }

          const height = this.$refs.multiselect.$el.querySelector('.multiselect__content-wrapper').offsetHeight;
          const standardOffset = 36;
          const emptyOffset = 30;
          const offsetNoData = 32;
          const isSearching = this.$refs.multiselect.$refs.list.querySelector('li').className !== '';

          if (this.options.length === 0 && isSearching) {
            actionSelector.style.top = `${standardOffset + offsetNoData}px`;
          } else if (isSearching) {
            actionSelector.style.top = `${height + emptyOffset}px`;
          } else {
            actionSelector.style.top = `${height + standardOffset}px`;
          }
        });
      },
      onActionClicked() {
        this.$emit('action-clicked');
        this.close();
      },
      onOpen() {
        if (this.action.length > 0) {
          this.showAction = true;
          this.updateActionOffset();
        }
        this.$emit('open');
      },
      onClose(event) {
        if (this.showAction === true && event === null) {
          this.$refs.multiselect.isOpen = true;
        } else {
          this.$emit('close');
          this.close();
        }
      },
      onSelect(selection) {
        if (this.showAction === true) {
          this.close();
        }
        this.$emit('select', selection);
      },
      onRemove(selection) {
        this.$emit('remove', selection);
      },
      onInput(input) {
        this.$emit('update:model-value', input);
        this.$emit('input', input);
      },
      onSearchChange(query) {
        this.updateActionOffset();
        this.$emit('search-change', query);
      },
      close() {
        this.showAction = false;
        this.$refs.multiselect.isOpen = false;
      },
    },
  };
</script>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>
<style scoped lang="scss">
    .multiselect-bootstrap-helper {
      flex: 1 1 auto;
      margin-bottom: 0;
    }

    :deep(.multiselect__content) {
      width: 100%;

      .multiselect__option {
        align-items: stretch;
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: 100%;
        white-space: nowrap;
        overflow: hidden;
        padding: 0 10px;
        text-overflow: ellipsis;
      }
    }

    :deep(.action) {
      width: 349px;
      z-index: 1;
      position: absolute;
      padding-top: 5px;
      padding-bottom: 5px;
      border-left-width: 1px;
      border-right-width: 1px;
      border-bottom-width: 1px;
      border-left-style: solid;
      border-right-style: solid;
      border-bottom-style: solid;

      &:hover {
        text-decoration: none;
      }

      .multiselect__option {
        padding-left: 14px;
      }
    }

    :deep(.multiselect__select) {
      top: 3px;
    }
</style>
